import {ActionIcon, Button, Divider, Group, Menu, Modal, ModalProps, Stack, Title} from "@mantine/core";
import {ServiceTypeModeDataTable} from "../../data-table/ServiceTypeModeDataTable/ServiceTypeModeDataTable";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {
  CompanyEntity,
  getObjectId,
  ServiceTypeEntity,
  serviceTypeModesSelector,
  useGetAllServiceTypeModesQuery
} from "@ots-frontend/ots-data";
import {Card, CardProps} from "../Card/Card";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useDisclosure} from "@mantine/hooks";
import {useServiceTypeModeMutation} from "../../../hooks/useServiceTypeModeMutation";
import {useForm} from "@mantine/form";
import {RefServiceModeSelect} from "../../select/entity/RefServiceModeSelect/RefServiceModeSelect";

export type ServiceTypeModeDataTableCardProps = CardProps & {
  company: CompanyEntity | string
  serviceType: ServiceTypeEntity | string
}

export const ServiceTypeModeDataTableCard = (props: ServiceTypeModeDataTableCardProps) => {
  const {
    company,
    serviceType,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const {t} = useTranslation(["common"])

  const [modalOpened, modalHandler] = useDisclosure(false);

  const selectServiceTypeModes = useMemo(() => {
    return serviceTypeModesSelector();
  }, []);

  const {
    serviceTypeModes,
    isServiceTypeModesLoading
  } = useGetAllServiceTypeModesQuery({
    companyId: getObjectId(company),
    serviceTypeId: getObjectId(serviceType),
  }, {
    selectFromResult: (result) => ({
      serviceTypeModes: selectServiceTypeModes(result, {}),
      isServiceTypeModesLoading: result.isLoading
    })
  });

  const {
    createServiceTypeMode,
    isCreating,
  } = useServiceTypeModeMutation()

  const onNewClick = useCallback(() => {
    modalHandler.open();
  }, [modalHandler])

  const onSubmit = useCallback((value: { mode: string } | undefined) => {
    if (value) {
      createServiceTypeMode({
        companyId: getObjectId(company),
        serviceTypeId: getObjectId(serviceType),
        modeCode: value.mode,
      }, {
        onSuccess: () => modalHandler.close()
      })
    }
  }, [createServiceTypeMode, company, serviceType, modalHandler])

  return (
      <Card shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>
        <Group position="apart" pb="sm">
          <Title order={4}>{t("common.entity.servicetypemode.plural", "Service Type Modes")}</Title>
          <Group spacing={4} position="left" noWrap>
            <HasAccess permissions={["servicetype_edit"]}>
              <Menu
                  width={260}
                  position="bottom-end"
                  transitionProps={{transition: "pop-top-right"}}
                  withinPortal
              >
                <Menu.Target>
                  <ActionIcon color="green">
                    <IconPlus size={16}/>
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={onNewClick}>
                    {t("common.actions.new", "New") + " " + t("common.entity.servicetypemode", "Service Type Mode")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </HasAccess>
          </Group>
        </Group>

        <Divider pb="sm"/>

        <ServiceTypeModeDataTable
            withBorder
            records={serviceTypeModes}
            fetching={isServiceTypeModesLoading || isCreating}
        />

        <ServiceTypeModeModal
            opened={modalOpened}
            onClose={modalHandler.close}
            title={t("common.entity.servicetypemode.plural", "Service Type Modes")}
            onSubmit={onSubmit}
        />

      </Card>
  )
}

interface ServiceTypeModeModalProps extends Omit<ModalProps, "onSubmit"> {
  onSubmit?: (value: { mode: string } | undefined) => void
}

const ServiceTypeModeModal = (props: ServiceTypeModeModalProps) => {
  const {
    onSubmit,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: {
      mode: undefined,
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        mode: values.mode!,
      })
      form.reset()
    }
  }, [form, onSubmit]);

  return (
      <Modal {...rest}>
        <form onSubmit={form.onSubmit(onFormSubmit)}>
          <Stack spacing="md">
            <RefServiceModeSelect
                label={t("common.entity.servicetypemode.form.field.mode", "Mode")}
                placeholder={t<string>("common.entity.servicetypemode.form.field.mode", "Mode")}
                pt="md"
                withinPortal={true}
                clearable
                withAsterisk
                required
                {...form.getInputProps("mode")} />
            <Button type="submit" fullWidth mt="xl">
              {t("common.action.submit", {ns: "common"})}
            </Button>
          </Stack>
        </form>
      </Modal>
  );
};
